'use client';

import { ComponentProps, FC, KeyboardEvent, MouseEvent, Ref, useState, useEffect } from 'react';

import { useAriaErrorMessageId } from '../../../../hooks/useAriaErrorMessageId/useAriaErrorMessageId';
import { useForwardedRef } from '../../../../hooks/useForwardedRef/useForwardedRef';
import useId from '../../../../hooks/useId/useId';
import dataAttributes from '../../../../utils/attributes/attributes';
import Format from '../../../../utils/format/format';
import { Badge } from '../../../badge/components/Badge/Badge';
import { Box } from '../../../box/components/Box/Box';
import { Icon } from '../../../icon/components/Icon/Icon';
import { List } from '../../../list/components/List/List';
import { ListItem } from '../../../list/components/ListItem/ListItem';
import { Menu } from '../../../menu/components/Menu/Menu';
import { Typography } from '../../../typography/components/Typography/Typography';

export interface DropdownOptionBadge {
   label: string;
   variant: ComponentProps<typeof Badge>['variant'];
   leadingIcon?: ComponentProps<typeof Badge>['leadingIcon'];
   trailingIcon?: ComponentProps<typeof Badge>['trailingIcon'];
}

export interface DropdownOption {
   id: string;
   value: string;
   badge?: DropdownOptionBadge;
   supportingText?: string;
   icon?: string;
}

interface Props {
   inputRef?: Ref<HTMLInputElement>;
   id?: string;
   name?: string;
   clearable?: boolean;
   disabled?: boolean;
   emptyOption?: boolean;
   error?: boolean;
   errorMessage?: string;
   label?: string;
   leadingIcon?: string;
   iconType?: 'flag' | 'default';
   options: Array<DropdownOption>;
   readOnly?: boolean;
   required?: boolean;
   supportText?: string;
   trailingIcon?: string;
   value?: DropdownOption;
   width?: string | number;
   autoFocus?: boolean;
   attributes?: Record<string, string>;
   onClear?: () => void;
   onChange?: (value?: DropdownOption) => void;
   onFocus?: () => void;
   onBlur?: () => void;
}

const getFocusedIndex = (options: Array<DropdownOption>, value?: DropdownOption) => {
   if (!value) return 0;
   const optionIndex = options.findIndex((optionToFind) => optionToFind.id === value.id);
   return optionIndex < 0 ? 0 : optionIndex;
}

export const Dropdown: FC<Props> = ({
   inputRef: refFromProps,
   id: idFromProps,
   name,
   options,
   clearable = false,
   disabled = false,
   emptyOption = true,
   error = false,
   errorMessage,
   label,
   leadingIcon,
   iconType = 'default',
   readOnly = false,
   required = false,
   supportText,
   trailingIcon,
   value,
   width,
   autoFocus,
   attributes = {},
   onClear,
   onChange,
   onBlur
}) => {
   const id = useId(idFromProps, 'dropdown');
   const errorMessageId = useAriaErrorMessageId(error, 'dropdown');

   const [menuOpen, setMenuOpen] = useState(false);
   const [focusedIndex, setFocusedIndex] = useState(() => getFocusedIndex(options, value));

   useEffect(() => {
      setFocusedIndex(getFocusedIndex(options, value));
   }, [value, options]);

   const inputRef = useForwardedRef(refFromProps);

   const getOptionId = (option: DropdownOption) => `${id}-${option.id}`;

   const activeOptionId = !menuOpen || !options[focusedIndex] ? undefined : getOptionId(options[focusedIndex]);

   const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (event.target === inputRef.current) {
         setMenuOpen((curr) => !curr);
      } else {
         event.preventDefault();
         inputRef.current?.focus();

         setMenuOpen((curr) => !curr);
      }
   };

   const handleChange = (option?: DropdownOption) => {
      onChange?.(option);
      setMenuOpen(false);
      if (option) {
         const optionIndex = options.findIndex((optionToFind) => optionToFind.id === option.id);
         setFocusedIndex(optionIndex);
      }
   };

   const handleOptionClick = (event: MouseEvent<HTMLElement>, option?: DropdownOption) => {
      event.preventDefault();
      handleChange(option);
   };

   const handleClear = () => {
      inputRef.current?.focus();
      onChange?.();
      onClear?.();
   };

   const handleBlur = () => {
      setMenuOpen(false);
      onBlur?.();
   };

   const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.code === 'Space') {
         event.stopPropagation();
         event.preventDefault();
         setMenuOpen(true);
      } else if (event.key === 'ArrowUp' && focusedIndex !== 0) {
         event.preventDefault();
         setFocusedIndex(focusedIndex - 1);
      } else if (event.key === 'ArrowDown' && focusedIndex !== options.length - 1) {
         event.preventDefault();
         setFocusedIndex(focusedIndex + 1);
      } else if (event.key === 'Enter') {
         handleChange(options[focusedIndex]);
      }
   };

   return (
      <div
         data-testid={`dropdown${error ? '--error' : ''}${readOnly ? '--read-only' : ''}${menuOpen ? '--open' : ''}`}
         style={{ width }}
         className={`gap-component-input-dropdown-container-spacing-outer-gap flex w-full flex-col ${disabled || readOnly ? 'pointer-events-none' : ''}`}
         {...dataAttributes(attributes)}
      >
         <Menu
            content={
               <div id={`${id}-menu`} data-testid="dropdown-menu" className="overflow-y-auto">
                  <List role="listbox">
                     {emptyOption && <ListItem role="option" ariaSelected={!!value && value.value === undefined} onClick={(e) => handleOptionClick(e, undefined)} />}
                     {options.map((option, index) => (
                        <ListItem
                           key={option.id}
                           attributes={{ 'test-selector': Format('dropdown-option', option.value) }}
                           role="option"
                           id={getOptionId(option)}
                           focused={focusedIndex === index}
                           ariaSelected={focusedIndex === index}
                           onClick={(e) => handleOptionClick(e, option)}
                           supportingText={option.supportingText}
                        >
                           <Box gap="sm" items="center" justify="between">
                              {option.icon && <Icon icon={option.icon} size="small" type={iconType} />}

                              <Typography whiteSpace="nowrap">{option.value}</Typography>
                              {option.badge && (
                                 <Badge key={option.badge.label} variant={option.badge.variant} leadingIcon={option.badge.leadingIcon} trailingIcon={option.badge.trailingIcon}>
                                    {option.badge.label}
                                 </Badge>
                              )}
                           </Box>
                        </ListItem>
                     ))}
                  </List>
               </div>
            }
            open={menuOpen}
            maxHeight
            width="fill"
            closeOnOutsideClick={false}
         >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div className="group relative w-full cursor-pointer" onMouseDown={handleClick}>
               {label && (
                  <label
                     data-testid="dropdown-label"
                     htmlFor={id}
                     style={{
                        maxWidth:
                           value || menuOpen
                              ? 'calc(100% -  2 * var(--component-input-dropdown-container-spacing-padding-x-no-icon))'
                              : 'calc(100% - 2* var(--component-input-dropdown-container-spacing-padding-x-no-icon) - var(--component-input-dropdown-container-spacing-gap) - var(--component-data-display-icon-container-sizing-width-body))'
                     }}
                     className={`absolute left-0 top-0 flex items-center transition-all cursor-pointer
                           ${!error && !disabled && (value || menuOpen) ? 'text-component-input-dropdown-label-color-text-neutral-enabled-as-label' : ''}
                           ${!error && !disabled ? 'group-focus-within:text-component-input-dropdown-label-color-text-neutral-focused' : ''}
                           ${!error && !disabled && menuOpen ? 'text-component-input-dropdown-label-color-text-neutral-focused' : ''}
                           ${!error && !disabled && !value && !menuOpen ? 'text-component-input-dropdown-label-color-text-neutral-enabled-as-placeholder' : ''}
                           ${disabled ? 'text-component-input-dropdown-label-color-text-neutral-disabled' : ''}
                           ${error && !disabled ? 'text-component-input-dropdown-label-color-text-error-enabled group-hover:!text-component-input-dropdown-label-color-text-error-hover' : ''}
                           ${
                              value || menuOpen
                                 ? 'bg-component-input-dropdown-container-color-background px-component-input-dropdown-label-spacing-padding-x h-[var(--component-input-dropdown-container-border-width-enabled)] group-focus-within:h-[var(--component-input-dropdown-container-border-width-focused)]'
                                 : 'h-full'
                           }
                           ${menuOpen ? 'h-[var(--component-input-dropdown-container-border-width-focused)]' : ''}
                           ${
                              !value && leadingIcon && !menuOpen
                                 ? 'phone:ml-[calc(var(--component-input-dropdown-container-spacing-padding-x-icon)+var(--component-input-dropdown-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-mobile-body))] tab-port:ml-[calc(var(--component-input-dropdown-container-spacing-padding-x-icon)+var(--component-input-dropdown-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] tab-land:ml-[calc(var(--component-input-dropdown-container-spacing-padding-x-icon)+var(--component-input-dropdown-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-tablet-body))] ml-[calc(var(--component-input-dropdown-container-spacing-padding-x-icon)+var(--component-input-dropdown-container-spacing-gap)+var(--component-data-display-icon-container-sizing-width-desktop-body))]'
                                 : 'ml-component-input-dropdown-container-spacing-padding-x-top-label'
                           }
                      `.replace(/\s+/g, ' ')}
                  >
                     <Typography variant={value || menuOpen ? 'caption' : 'body'} color="inherit" maxLines="1">
                        {label}
                     </Typography>
                  </label>
               )}

               <div
                  data-testid="dropdown-container"
                  role="presentation"
                  className={`min-h-component-input-dropdown-container-sizing-min-height pr-component-input-dropdown-container-spacing-padding-x-icon py-component-input-dropdown-container-spacing-padding-y gap-component-input-dropdown-container-spacing-gap bg-component-input-dropdown-container-color-background rounded-component-input-dropdown-container-border-radius border-component-input-dropdown-container-border-width-enabled focus-within:!border-component-input-dropdown-container-border-width-focused box-border flex items-start justify-between transition-all
                           ${
                              error
                                 ? '!border-component-input-dropdown-container-color-border-error-enabled hover:!border-component-input-dropdown-container-color-border-error-hover focus-within:!border-component-input-dropdown-container-color-border-error-enabled'
                                 : '!border-component-input-dropdown-container-color-border-neutral-enabled hover:!border-component-input-dropdown-container-color-border-neutral-hover focus-within:!border-component-input-dropdown-container-color-border-neutral-focused'
                           }
                           ${menuOpen ? '!border-component-input-dropdown-container-border-width-focused' : ''}
                           ${menuOpen && error ? '!border-component-input-dropdown-container-color-border-error-enabled' : ''}
                           ${menuOpen && !error ? '!border-component-input-dropdown-container-color-border-neutral-enabled' : ''}
                           ${leadingIcon ? 'pl-component-input-dropdown-container-spacing-padding-x-icon' : 'pl-component-input-dropdown-container-spacing-padding-x-no-icon'}
                  `.replace(/\s+/g, ' ')}
               >
                  {leadingIcon && (
                     <div
                        data-testid="dropdown-leading-icon"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                           disabled ? 'text-component-input-dropdown-icon-start-color-text-on-surface-disabled' : 'text-component-input-dropdown-icon-start-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon={leadingIcon} type={iconType} />
                     </div>
                  )}
                  <div className="flex h-full flex-1 items-center">
                     <input
                        role="combobox"
                        data-testid="dropdown-input"
                        className="text-component-input-dropdown-input-color-text-on-surface-enabled disabled:text-component-input-dropdown-input-color-text-on-surface-disabled typography-component-typography-body-regular-md w-full cursor-pointer bg-transparent outline-none"
                        disabled={disabled}
                        id={id}
                        name={name}
                        readOnly
                        ref={inputRef}
                        required={required}
                        aria-controls={`${id}-menu`}
                        aria-expanded={menuOpen}
                        aria-activedescendant={activeOptionId}
                        aria-autocomplete="none"
                        aria-invalid={error}
                        aria-errormessage={errorMessageId}
                        autoFocus={autoFocus}
                        value={value?.value ?? ''}
                        onKeyDown={handleKeyPress}
                        onBlur={handleBlur}
                     />
                  </div>
                  {value?.badge && (
                     <Badge key={value.badge.label} variant={value.badge.variant} leadingIcon={value.badge.leadingIcon} trailingIcon={value.badge.trailingIcon}>
                        {value.badge.label}
                     </Badge>
                  )}
                  {clearable && value && (
                     <div
                        data-testid="dropdown-clear"
                        onClick={handleClear}
                        role="presentation"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center cursor-pointer ${
                           disabled ? 'text-component-input-search-icon-end-color-text-on-surface-disabled' : 'text-component-input-search-icon-end-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon="times-circle" />
                     </div>
                  )}
                  {trailingIcon && (
                     <div
                        data-testid="dropdown-trailing-icon"
                        className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center ${
                           disabled ? 'text-component-input-dropdown-icon-end-color-text-on-surface-disabled' : 'text-component-input-dropdown-icon-end-color-text-on-surface-enabled'
                        }`.replace(/\s+/g, ' ')}
                     >
                        <Icon icon={trailingIcon} />
                     </div>
                  )}
                  <div
                     data-testid="dropdown-chevron"
                     role="presentation"
                     className={`w-component-data-display-icon-container-sizing-size-body m-auto flex aspect-square h-full items-center justify-center transition-all ${
                        disabled ? 'text-component-input-dropdown-icon-end-color-text-on-surface-disabled' : 'text-component-input-dropdown-icon-end-color-text-on-surface-enabled'
                     } ${menuOpen ? 'rotate-180' : ''}`.replace(/\s+/g, ' ')}
                  >
                     <Icon icon="chevron-down" />
                  </div>
               </div>
            </div>
         </Menu>
         {supportText && (
            <div
               data-testid="dropdown-support-text"
               className={`pl-component-input-dropdown-supporting-text-spacing-padding-left gap-component-input-dropdown-supporting-text-spacing-gap flex items-center

                        ${!disabled ? 'text-component-input-dropdown-supporting-text-color-text-neutral-enabled' : ''}
                        ${disabled ? 'text-component-input-dropdown-supporting-text-color-text-neutral-disabled' : ''}
                        `.replace(/\s+/g, ' ')}
            >
               {supportText && (
                  <Typography variant="caption" color="inherit">
                     {supportText}
                  </Typography>
               )}
            </div>
         )}
         {error && errorMessage && (
            <div data-testid="dropdown-error-message" className="pl-component-input-dropdown-supporting-text-spacing-padding-left gap-component-input-dropdown-supporting-text-spacing-gap text-component-input-dropdown-supporting-text-color-text-error flex items-center">
               <Icon icon="exclamation-circle" size="caption" />
               <Typography id={errorMessageId} variant="caption" color="inherit">
                  {errorMessage}
               </Typography>
            </div>
         )}
      </div>
   );
};
